import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import { useT } from "@/i18n/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = {
  combined: { subscribers: "45K+", views: "2.1M+", videos: "250+", years_active: "1" },
  channels: [
    {
      slug: "viver-na-aldeia",
      name: "Viver na Aldeia",
      tagline: "Slow Living, Terra e Tradição.",
      subscribers: "30.000+",
      views: "500.000",
      videos: "50",
      accent: "#C47A4D",
    },
    {
      slug: "trilhos-em-furia",
      name: "Trilhos em Fúria",
      tagline: "Exploração e montanha.",
      subscribers: "14.200+",
      views: "1.500.000",
      videos: "133",
      accent: "#5C8041",
    },
  ],
};

export default function StatsBento() {
  const { t } = useT();
  const [data, setData] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => r.data && setData(r.data)).catch(() => {});
  }, []);

  const c = data.combined || FALLBACK.combined;

  return (
    <section data-testid="stats-section" className="tt-section relative">
      <div className="mx-auto max-w-[1480px] px-6 md:px-12">
        <div className="mb-16 max-w-2xl">
          <p className="tt-overline">{t("stats.overline")}</p>
          <h2 className="mt-4 font-serif text-4xl md:text-6xl leading-[1.02] text-[var(--tt-text)]">
            {t("stats.heading_1")}{" "}
            <em className="italic text-[var(--tt-gold)]">{t("stats.heading_2")}</em>
            {t("stats.heading_3")}
          </h2>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 lg:col-span-7 tt-trace-border rounded-sm"
          >
            <div className="relative tt-glass p-8 md:p-12 rounded-sm h-full flex flex-col justify-between">
              <div>
                <p className="tt-overline">{t("stats.combined")}</p>
                <p className="mt-4 font-serif text-[14vw] md:text-[8vw] lg:text-[6.5vw] leading-[0.9] text-[var(--tt-text)] tracking-tighter">
                  {c.subscribers}
                </p>
                <p className="mt-2 font-serif italic text-xl md:text-2xl text-[var(--tt-gold)]">
                  {t("stats.subscribers_label")}
                </p>
              </div>
              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[var(--tt-border)] pt-8">
                <Stat label={t("stats.views")} value={c.views} />
                <Stat label={t("stats.videos")} value={c.videos} />
                <Stat label={t("stats.years")} value={c.years_active} />
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 lg:col-span-5 grid grid-cols-1 gap-4 md:gap-6">
            {(data.channels || FALLBACK.channels).map((ch, idx) => (
              <motion.div
                key={ch.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.15 + idx * 0.1 }}
                data-testid={`stat-card-${ch.slug}`}
                className="relative rounded-sm border border-[var(--tt-border)] tt-glass p-6 md:p-8 overflow-hidden tt-channel-card"
              >
                <span
                  className="absolute top-0 left-0 w-full h-px"
                  style={{ background: `linear-gradient(90deg, ${ch.accent}, transparent)` }}
                />
                <div className="flex items-start justify-between">
                  <div>
                    <p className="tt-overline" style={{ color: ch.accent }}>
                      {ch.name}
                    </p>
                    <p className="mt-2 font-serif italic text-base text-[var(--tt-text-dim)]">
                      {ch.tagline}
                    </p>
                  </div>
                  <span
                    className="w-2 h-2 rounded-full mt-2"
                    style={{
                      background: ch.accent,
                      boxShadow: `0 0 18px ${ch.accent}`,
                      animation: "tt-pulse 2.6s ease-in-out infinite",
                    }}
                  />
                </div>

                <div className="mt-6 grid grid-cols-3 gap-4">
                  <Stat label={t("stats.subs_short")} value={ch.subscribers} compact />
                  <Stat label={t("stats.views_short")} value={ch.views} compact />
                  <Stat label={t("stats.videos_short")} value={ch.videos} compact />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, compact }) {
  return (
    <div>
      <p
        className={`font-serif text-[var(--tt-text)] tracking-tight ${
          compact ? "text-2xl md:text-3xl" : "text-3xl md:text-4xl"
        }`}
      >
        {value}
      </p>
      <p className="tt-overline mt-1 text-[10px]">{label}</p>
    </div>
  );
}
